import api from '@/shared/utils/apiConfig';
import { recoverIfMutationFinished } from '@/shared/utils/apiError';
import type { ApiResponse } from '@/shared/types/api';
import type { KpiPayoutRule } from '../types/kpiBonus.types';

export type KpiMetricKind = 'system' | 'custom';
export type KpiMetricSource = 'auto' | 'manual';
export type KpiMetricUnit = 'count' | 'money' | 'days';
export type KpiCycleStatus = 'draft' | 'locked' | 'closed';
export type KpiSheetStatus = KpiCycleStatus;
export type KpiActualSource = 'auto' | 'manual';
export const JOURNAL_WORK_ROLES = ['work', 'performed', 'supported', 'inner', 'outer'] as const;
export type JournalWorkRole = (typeof JOURNAL_WORK_ROLES)[number];
export const KPI_TEMPLATE_AUDIENCES = ['all', 'department', 'staff'] as const;
export type KpiTemplateAudience = (typeof KPI_TEMPLATE_AUDIENCES)[number];

export interface KpiMetric {
  id: string;
  name: string;
  kind: KpiMetricKind;
  source: KpiMetricSource;
  unit: KpiMetricUnit;
  autoCode?: string;
  catalogItemId?: string;
  catalogItemName?: string;
  procedureStepOrder?: number;
  journalItemId?: string;
  journalItemName?: string;
  journalWorkRole?: JournalWorkRole;
  departmentId?: string;
  departmentName?: string;
  departmentIds?: string[];
  departmentNames?: string[];
  weight: number;
  isActive: boolean;
  createdBy?: { id: string; name: string } | null;
  updatedBy?: { id: string; name: string } | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface KpiAssignment {
  id: string;
  sheetId: string;
  staffId: string;
  staffName?: string;
  metricId: string;
  metricName?: string;
  metricSource?: KpiMetricSource;
  metricUnit?: KpiMetricUnit;
  metricWeight?: number;
  periodKey: string;
  target: number;
  actual: number;
  actualSource: KpiActualSource;
  percent: number;
  locked: boolean;
  payoutAmount: number;
  payoutRateSnapshot: number;
  payoutSnapshot?: {
    mode: string;
    matchKey: string;
    policyScope: string;
    audience?: string;
    departmentId?: string;
    staffId?: string;
    effectiveFrom?: string;
    effectiveTo?: string | null;
    actual: number;
    target: number;
    percent: number;
    unitAmount?: number;
    selectedMinPercent?: number;
    selectedMinActual?: number;
    amount: number;
  };
  payrollConsumed: boolean;
}

export interface KpiSheet {
  id: string;
  cycleId: string;
  cycleName?: string;
  name: string;
  periodKey: string;
  status: KpiSheetStatus;
  clinicId: string;
  clinicName?: string;
  staffId: string;
  staffName?: string;
  staffCode?: string;
  firstName?: string;
  lastName?: string;
  designation?: string;
  avatarUrl?: string | null;
  departmentId?: string;
  departmentName?: string;
  metricIds: string[];
  templateId?: string;
  templateName?: string;
  metrics?: KpiMetric[];
  assignments?: KpiAssignment[];
  staffScore?: number;
  sheetScore?: number;
  payoutTotal?: number;
  createdById?: string;
  createdBy?: { id: string; name: string } | null;
  updatedBy?: { id: string; name: string } | null;
  lockedAt?: string;
  lockedBy?: { id: string; name: string } | null;
  closedAt?: string;
  closedBy?: { id: string; name: string } | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface KpiCycle {
  id: string;
  name: string;
  periodKey: string;
  status: KpiCycleStatus;
  clinicId: string;
  clinicName?: string;
  sheetCount?: number;
  avgScore?: number;
  sheets?: KpiSheet[];
  createdById?: string;
  createdBy?: { id: string; name: string };
  updatedBy?: { id: string; name: string };
  lockedAt?: string;
  lockedById?: string;
  lockedBy?: { id: string; name: string };
  closedAt?: string;
  closedById?: string;
  closedBy?: { id: string; name: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface MyKpiResponse {
  periodKey: string;
  sheet: KpiSheet | null;
  assignments: KpiAssignment[];
  staffScore: number | null;
}

export interface CreateKpiMetricPayload {
  name: string;
  source: 'manual';
  unit?: KpiMetricUnit;
  weight?: number;
  departmentId?: string | null;
}

export interface CreateKpiCyclePayload {
  name: string;
  periodKey: string;
  clinicId: string;
}

export interface UpdateKpiCyclePayload {
  name?: string;
}

export interface GenerateKpiCyclePayload {
  templateId: string;
  staffIds: string[];
}

export interface CloneKpiCyclePayload {
  periodKey: string;
  name?: string;
}

export interface UpdateKpiSheetPayload {
  name?: string;
  metricIds?: string[];
  targets?: Record<string, number>;
  templateId?: string | null;
}

export interface KpiTemplateItem {
  metricId: string;
  defaultTarget: number;
  payout: KpiPayoutRule;
  metric?: KpiMetric;
}

export interface KpiTemplate {
  id: string;
  name: string;
  description?: string;
  audience: KpiTemplateAudience;
  departmentId?: string;
  departmentName?: string;
  staffIds: string[];
  staffNames: string[];
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
  items: KpiTemplateItem[];
  createdById?: string;
  createdBy?: { id: string; name: string } | null;
  updatedBy?: { id: string; name: string } | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateKpiTemplatePayload {
  name: string;
  description?: string;
  audience: KpiTemplateAudience;
  departmentId?: string | null;
  staffIds?: string[];
  effectiveFrom: string;
  effectiveTo?: string | null;
  items: Array<{ metricId: string; defaultTarget?: number; payout: KpiPayoutRule }>;
  isActive?: boolean;
}

export interface UpdateKpiTemplatePayload {
  name?: string;
  description?: string | null;
  audience?: KpiTemplateAudience;
  departmentId?: string | null;
  staffIds?: string[];
  effectiveFrom?: string;
  effectiveTo?: string | null;
  items?: Array<{ metricId: string; defaultTarget?: number; payout: KpiPayoutRule }>;
  isActive?: boolean;
}

export interface KpiCycleListParams {
  search?: string;
  period?: string;
  status?: KpiCycleStatus | string;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface KpiSheetListParams {
  cycle?: string;
  period?: string;
  staff?: string;
  search?: string;
  status?: KpiSheetStatus | string;
  page?: number;
  limit?: number;
  sort?: string;
}

export const kpiAPI = {
  getMetrics: async (params?: { includeInactive?: boolean; sort?: string }): Promise<KpiMetric[]> => {
    const response = await api.get<ApiResponse<KpiMetric[]>>('/kpi/metrics', { params });
    return response.data.data ?? [];
  },

  createMetric: async (payload: CreateKpiMetricPayload): Promise<KpiMetric> => {
    const response = await api.post<ApiResponse<KpiMetric>>('/kpi/metrics', payload);
    return response.data.data as KpiMetric;
  },

  updateMetric: async (
    id: string,
    payload: {
      name?: string;
      unit?: KpiMetricUnit;
      weight?: number;
      isActive?: boolean;
      departmentId?: string | null;
    }
  ): Promise<KpiMetric> => {
    const response = await api.put<ApiResponse<KpiMetric>>(`/kpi/metrics/${id}`, payload);
    return response.data.data as KpiMetric;
  },

  deactivateMetric: async (id: string): Promise<KpiMetric> => {
    const response = await api.delete<ApiResponse<KpiMetric>>(`/kpi/metrics/${id}`);
    return response.data.data as KpiMetric;
  },

  getCycles: async (
    params: KpiCycleListParams = {}
  ): Promise<{ data: KpiCycle[]; total: number; page: number; pages: number }> => {
    const response = await api.get<
      ApiResponse<KpiCycle[]> & { total?: number; page?: number; pages?: number }
    >('/kpi/cycles', { params });
    return {
      data: response.data.data ?? [],
      total: response.data.total ?? 0,
      page: response.data.page ?? 1,
      pages: response.data.pages ?? 1,
    };
  },

  getCycle: async (id: string, options?: { timeout?: number }): Promise<KpiCycle> => {
    const response = await api.get<ApiResponse<KpiCycle>>(`/kpi/cycles/${id}`, {
      timeout: options?.timeout,
    });
    return response.data.data as KpiCycle;
  },

  createCycle: async (payload: CreateKpiCyclePayload): Promise<KpiCycle> => {
    const response = await api.post<ApiResponse<KpiCycle>>('/kpi/cycles', payload);
    return response.data.data as KpiCycle;
  },

  updateCycle: async (id: string, payload: UpdateKpiCyclePayload): Promise<KpiCycle> => {
    const response = await api.put<ApiResponse<KpiCycle>>(`/kpi/cycles/${id}`, payload);
    return response.data.data as KpiCycle;
  },

  generateCycleSheets: async (
    id: string,
    payload: GenerateKpiCyclePayload
  ): Promise<KpiCycle> => {
    const response = await api.post<ApiResponse<KpiCycle>>(`/kpi/cycles/${id}/generate`, payload);
    return response.data.data as KpiCycle;
  },

  cloneCycle: async (id: string, payload: CloneKpiCyclePayload): Promise<KpiCycle> => {
    const response = await api.post<ApiResponse<KpiCycle>>(`/kpi/cycles/${id}/clone`, payload);
    return response.data.data as KpiCycle;
  },

  lockCycle: async (id: string): Promise<KpiCycle> => {
    try {
      const response = await api.post<ApiResponse<KpiCycle>>(`/kpi/cycles/${id}/lock`);
      return response.data.data as KpiCycle;
    } catch (err) {
      return recoverIfMutationFinished(
        err,
        () => kpiAPI.getCycle(id, { timeout: 30_000 }),
        (cycle) => cycle.status === 'locked'
      );
    }
  },

  unlockCycle: async (id: string): Promise<KpiCycle> => {
    try {
      const response = await api.post<ApiResponse<KpiCycle>>(`/kpi/cycles/${id}/unlock`);
      return response.data.data as KpiCycle;
    } catch (err) {
      return recoverIfMutationFinished(
        err,
        () => kpiAPI.getCycle(id, { timeout: 30_000 }),
        (cycle) => cycle.status === 'draft'
      );
    }
  },

  closeCycle: async (id: string): Promise<KpiCycle> => {
    try {
      const response = await api.post<ApiResponse<KpiCycle>>(`/kpi/cycles/${id}/close`);
      return response.data.data as KpiCycle;
    } catch (err) {
      return recoverIfMutationFinished(
        err,
        () => kpiAPI.getCycle(id, { timeout: 30_000 }),
        (cycle) => cycle.status === 'closed'
      );
    }
  },

  reopenCycle: async (id: string): Promise<KpiCycle> => {
    try {
      const response = await api.post<ApiResponse<KpiCycle>>(`/kpi/cycles/${id}/reopen`);
      return response.data.data as KpiCycle;
    } catch (err) {
      return recoverIfMutationFinished(
        err,
        () => kpiAPI.getCycle(id, { timeout: 30_000 }),
        (cycle) => cycle.status === 'locked'
      );
    }
  },

  recalculateCycle: async (id: string): Promise<KpiCycle> => {
    const response = await api.post<ApiResponse<KpiCycle>>(`/kpi/cycles/${id}/recalculate`);
    return response.data.data as KpiCycle;
  },

  getSheets: async (
    params: KpiSheetListParams = {}
  ): Promise<{ data: KpiSheet[]; total: number; page: number; pages: number }> => {
    const response = await api.get<
      ApiResponse<KpiSheet[]> & { total?: number; page?: number; pages?: number }
    >('/kpi/sheets', { params });
    return {
      data: response.data.data ?? [],
      total: response.data.total ?? 0,
      page: response.data.page ?? 1,
      pages: response.data.pages ?? 1,
    };
  },

  getSheet: async (id: string): Promise<KpiSheet> => {
    const response = await api.get<ApiResponse<KpiSheet>>(`/kpi/sheets/${id}`);
    return response.data.data as KpiSheet;
  },

  updateSheet: async (id: string, payload: UpdateKpiSheetPayload): Promise<KpiSheet> => {
    const response = await api.put<ApiResponse<KpiSheet>>(`/kpi/sheets/${id}`, payload);
    return response.data.data as KpiSheet;
  },

  getTemplates: async (params?: { includeInactive?: boolean; sort?: string }): Promise<KpiTemplate[]> => {
    const response = await api.get<ApiResponse<KpiTemplate[]>>('/kpi/templates', { params });
    return response.data.data ?? [];
  },

  getTemplate: async (id: string): Promise<KpiTemplate> => {
    const response = await api.get<ApiResponse<KpiTemplate>>(`/kpi/templates/${id}`);
    return response.data.data as KpiTemplate;
  },

  createTemplate: async (payload: CreateKpiTemplatePayload): Promise<KpiTemplate> => {
    const response = await api.post<ApiResponse<KpiTemplate>>('/kpi/templates', payload);
    return response.data.data as KpiTemplate;
  },

  updateTemplate: async (
    id: string,
    payload: UpdateKpiTemplatePayload
  ): Promise<KpiTemplate> => {
    const response = await api.put<ApiResponse<KpiTemplate>>(`/kpi/templates/${id}`, payload);
    return response.data.data as KpiTemplate;
  },

  deactivateTemplate: async (id: string): Promise<KpiTemplate> => {
    const response = await api.delete<ApiResponse<KpiTemplate>>(`/kpi/templates/${id}`);
    return response.data.data as KpiTemplate;
  },

  cloneTemplate: async (id: string): Promise<KpiTemplate> => {
    const response = await api.post<ApiResponse<KpiTemplate>>(`/kpi/templates/${id}/clone`);
    return response.data.data as KpiTemplate;
  },

  updateAssignmentActual: async (id: string, actual: number): Promise<KpiAssignment> => {
    const response = await api.patch<ApiResponse<KpiAssignment>>(`/kpi/assignments/${id}/actual`, {
      actual,
    });
    return response.data.data as KpiAssignment;
  },

  updateAssignmentTarget: async (id: string, target: number): Promise<KpiAssignment> => {
    const response = await api.patch<ApiResponse<KpiAssignment>>(`/kpi/assignments/${id}/target`, {
      target,
    });
    return response.data.data as KpiAssignment;
  },

  getMyKpi: async (period?: string): Promise<MyKpiResponse> => {
    const response = await api.get<ApiResponse<MyKpiResponse>>('/kpi/me', {
      params: period ? { period } : undefined,
    });
    return response.data.data as MyKpiResponse;
  },
};
